import {
  ReactNode,
  RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DialogContext } from './DialogContext';

interface Props {
  close: () => void;
  dialogRef: RefObject<HTMLDialogElement | null>;
  children: ReactNode;
  opened: boolean;
  rootPage: string;
}

export function DialogContextProvider(props: Props) {
  const [currentPage, setCurrentPage] = useState<string>(props.rootPage);
  const [nextPages, setNextPages] = useState<Set<string>>(new Set());
  const [prevPages, setPrevPages] = useState<Array<string>>([]);
  const pages = useRef<Set<string>>(new Set());

  // If closed reset state to initial state
  useEffect(() => {
    if (!props.opened && prevPages.length) {
      setCurrentPage(props.rootPage);
      setPrevPages([]);
      setNextPages(
        // Excludes root page
        new Set([...pages.current].filter((page) => page !== props.rootPage))
      );
    }
  }, [prevPages.length, props.opened, props.rootPage]);

  // Focus dialog when current is updated
  useEffect(() => {
    props.dialogRef.current?.focus();
  }, [currentPage, props.dialogRef]);

  const value = useMemo(
    () => ({
      close: props.close,
      currentPage,
      nextPages,
      pages: pages.current,
      opened: props.opened,
      prevPages,
      rootPage: props.rootPage,
      setCurrentPage,
      setNextPages,
      setPrevPages,
    }),
    [
      props.close,
      props.opened,
      props.rootPage,
      currentPage,
      nextPages,
      prevPages,
    ]
  );

  return (
    <DialogContext.Provider value={value}>
      {props.opened && props.children}
    </DialogContext.Provider>
  );
}
